import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Activity, GaugeCircle, Network, Cpu, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background cr-grid relative overflow-hidden">
      {/* ambient glow */}
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-status-critical/5 blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-10">
        {/* top bar */}
        <div className="flex items-center justify-between border-b border-border/60 pb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-heading font-extrabold tracking-tight text-lg leading-none">CONTROL ROOM SIM</div>
              <div className="label-ot mt-1">OT Security Training Simulator</div>
            </div>
          </div>
          <div className="font-mono text-xs text-muted-foreground hidden sm:flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-status-normal animate-pulse" /> SYSTEM ONLINE
          </div>
        </div>

        {/* hero */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mt-16 lg:mt-24">
          <div>
            <div className="label-ot text-primary mb-4">OIL &amp; GAS · MANUFACTURING · SCADA · DCS · PLC/HMI</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Train on real OT systems.
              <span className="block text-primary">Break nothing real.</span>
            </h1>
            <p className="text-muted-foreground text-base mt-6 max-w-md leading-relaxed">
              A browser-based industrial control room simulator for OT security education. Drive live process
              mimics, respond to alarms, and run red / blue team exercises — fully graded, fully logged.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button
                data-testid="login-google-button"
                onClick={handleLogin}
                className="rounded-sm font-semibold h-12 px-7 text-base bg-primary hover:bg-primary/90 group"
              >
                Enter Control Room
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <span className="font-mono text-xs text-muted-foreground">Sign in with Google</span>
            </div>
          </div>

          {/* feature panel */}
          <div className="bg-card/70 border border-border/60 rounded-md p-1">
            <div className="bg-popover border-b border-border/60 px-4 py-2.5 flex items-center justify-between rounded-t-sm">
              <span className="font-mono text-xs text-muted-foreground">NORTHRIDGE_PIPELINE.hmi</span>
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-status-critical/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-status-warning/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-status-normal/70" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-border/40">
              {[
                { icon: Cpu, t: "PLC + HMI", d: "Process mimic & tag control" },
                { icon: Network, t: "SCADA", d: "Plant-wide overview & trends" },
                { icon: GaugeCircle, t: "DCS", d: "Faceplate control loops" },
                { icon: Activity, t: "Red / Blue", d: "Attack & defend, graded" },
              ].map((f, i) => (
                <div key={i} className="bg-card p-5">
                  <f.icon className="w-5 h-5 text-primary mb-3" />
                  <div className="font-heading font-semibold text-sm">{f.t}</div>
                  <div className="text-xs text-muted-foreground mt-1">{f.d}</div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 font-mono text-[11px] text-muted-foreground flex justify-between border-t border-border/40">
              <span>PT-102 <span className="text-status-normal">882 psi</span></span>
              <span>FT-201 <span className="text-status-normal">5204 bbl/h</span></span>
              <span>ESD-01 <span className="text-status-normal">ARMED</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
